import { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Combobox } from '../ui/combobox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Plus, Check, Loader2, AlertTriangle, Trash2, ShoppingCart
} from 'lucide-react';

const DispensacaoNaoPrescritoModal = ({
  open, onOpenChange, estoqueItens, onConfirm, saving,
}) => {
  const [form, setForm] = useState({ item_id: '', quantidade: 1, justificativa: '' });
  const [cart, setCart] = useState([]);

  const handleAddToCart = () => {
    if (!form.item_id) return;
    if (!form.quantidade || form.quantidade <= 0) return;
    const item = estoqueItens.find(i => i.id === form.item_id);
    setCart(prev => [...prev, {
      _id: Date.now() + Math.random(),
      item_id: form.item_id,
      item_nome: item?.nome || '',
      item_unidade: item?.unidade || 'un',
      item_tipo: item?.tipo || 'material',
      quantidade: parseFloat(form.quantidade),
    }]);
    setForm(f => ({ ...f, item_id: '', quantidade: 1 }));
  };

  const handleRemoveFromCart = (id) => {
    setCart(prev => prev.filter(i => i._id !== id));
  };

  const handleConfirm = async () => {
    await onConfirm(cart, form.justificativa.trim());
    setCart([]);
    setForm({ item_id: '', quantidade: 1, justificativa: '' });
  };

  const handleOpenChange = (val) => {
    if (!val) {
      setCart([]);
      setForm({ item_id: '', quantidade: 1, justificativa: '' });
    }
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Dispensar itens não prescritos
          </DialogTitle>
          <DialogDescription>
            Adicione os itens ao carrinho e confirme a dispensação de uma vez.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-12 gap-2 items-end">
            <div className="col-span-6 space-y-1">
              <Label className="text-sm">Item *</Label>
              <Combobox
                options={estoqueItens.map(i => ({
                  value: i.id,
                  label: `${i.nome} (${i.estoque_atual} ${i.unidade})`,
                  subtitle: i.tipo === 'medicamento' ? 'Medicamento' : 'Material',
                }))}
                value={form.item_id}
                onValueChange={(v) => setForm(f => ({ ...f, item_id: v }))}
                placeholder="Buscar item..."
                searchPlaceholder="Digite o nome..."
                emptyText="Nenhum item encontrado"
                data-testid="select-np-item"
              />
            </div>
            <div className="col-span-3 space-y-1">
              <Label className="text-sm">Qtd *</Label>
              <Input
                type="number" step="0.5" min="0.5"
                value={form.quantidade}
                onChange={(e) => setForm(f => ({ ...f, quantidade: e.target.value }))}
                data-testid="input-np-quantidade"
              />
            </div>
            <div className="col-span-3">
              <Button
                variant="outline" className="w-full gap-1"
                onClick={handleAddToCart}
                disabled={!form.item_id}
                data-testid="btn-add-np"
              >
                <Plus className="h-4 w-4" /> Adicionar
              </Button>
            </div>
          </div>

          {cart.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                <span className="text-sm font-medium">Carrinho ({cart.length} {cart.length === 1 ? 'item' : 'itens'})</span>
              </div>
              <div className="divide-y max-h-40 overflow-y-auto">
                {cart.map((entry) => (
                  <div key={entry._id} className="flex items-center justify-between px-3 py-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {entry.item_tipo === 'medicamento' ? 'MED' : 'MAT'}
                      </Badge>
                      <span className="text-sm">{entry.item_nome}</span>
                      <span className="text-xs text-muted-foreground">x{entry.quantidade} {entry.item_unidade}</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => handleRemoveFromCart(entry._id)} className="h-7 w-7 p-0 text-red-500">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label className="font-semibold text-red-700">Justificativa *</Label>
            <Textarea
              value={form.justificativa}
              onChange={(e) => setForm(f => ({ ...f, justificativa: e.target.value }))}
              placeholder="Descreva o motivo da dispensação fora da prescrição..."
              rows={3}
              className="resize-none"
              data-testid="textarea-np-justificativa"
            />
            {form.justificativa.length > 0 && form.justificativa.trim().length < 5 && (
              <p className="text-xs text-red-500">Mínimo 5 caracteres</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={saving || cart.length === 0 || form.justificativa.trim().length < 5}
            className="bg-red-600 hover:bg-red-700"
            data-testid="btn-confirmar-np"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Confirmar dispensação ({cart.length} {cart.length === 1 ? 'item' : 'itens'})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DispensacaoNaoPrescritoModal;
